/**
 * BgtSpcRtsoDpstDialog.tsx
 * ------------------------------------------------------------
 * - (기준) BgtAtrzRtsoDpstDialog(div 구조/폭 400) 스타일로 통일
 * - (데이터 기준) BgtSpcDpstViewDialog에서 쓰는 필드(첨부/반려 포함) 그대로 표시
 * - 기능 로직(조회/확인/반려 모달) 유지
 * - 타이틀/닫기 버튼 없음(부모에서 감싸는 형태 전제)
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Button from "@/js/common/ui/Button";

import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";

import BudgetViewFile from "../../budgetFileBox/BudgetViewFile";
import { budgetFilesProps } from "@/js/common/types/types";

import BgtAtrzRejectReason from "./BgtAtrzRejectReason";

interface Props {
  spcAcntgDpstDocId: string;
  atrzId: string;
  atrzLineId: string;
  setCallTime: Function;
  onDone?: () => void;
}

const BgtSpcRtsoDpstDialog = ({ spcAcntgDpstDocId, atrzId, atrzLineId, setCallTime, onDone}: Props) => {
  const [dpstRsdocNo, setDpstRsdocNo] = React.useState<string>("");
  const [depositDt, setDepositDt] = React.useState<string>("");
  const [kwanCd, setKwanCd] = React.useState<string>("");
  const [hangCd, setHangCd] = React.useState<string>("");
  const [mokCd, setMokCd] = React.useState<string>("");
  const [summary, setSummary] = React.useState<string>("");
  const [memo, setMemo] = React.useState<string>("");
  const [spcAcntgSeCd, setSpcAcntgSeCd] = React.useState<string>("");
  const [amount, setAmount] = React.useState<string>("");

  // 반려 표시용 + 첨부파일
  const [atrzSttsCd, setAtrzSttsCd] = React.useState<string>("");
  const [atrzOpnnCn, setAtrzOpnnCn] = React.useState<string>("");
  const [savedSpcDpstFiles, setSavedSpcDpstFiles] = React.useState<budgetFilesProps[]>([]);

  const [confirming, setConfirming] = React.useState(false);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);

  const handleReset = () => {
    setDpstRsdocNo("");
    setDepositDt("");
    setKwanCd("");
    setHangCd("");
    setMokCd("");
    setSummary("");
    setMemo("");
    setAmount("");
    setSpcAcntgSeCd("");
    setAtrzSttsCd("");
    setAtrzOpnnCn("");
    setSavedSpcDpstFiles([]);
  };

  React.useEffect(() => {
    if (Util.isEmpty(spcAcntgDpstDocId)) {
      handleReset();
      return;
    }

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/spc/select-aprvr-spc-dpst-dtl.do",
      methodType: "post",
      paramType: "json",
      paramData: { spcAcntgDpstDocId: String(spcAcntgDpstDocId) },
      onSuccessFn: (res: any) => {
        const result = res?.result || {};
        const item = result?.item || {};

        const dpstDtRaw = String(item.dpstDt);
        setDepositDt(dpstDtRaw.includes("-") ? dpstDtRaw : Util.cvtDateStrForm(dpstDtRaw, "-", "YYYYMMDD"));

        setDpstRsdocNo(String(item.dpstRsdocNo));
        setKwanCd(String(item.bgtSbjct1stCd));
        setHangCd(String(item.bgtSbjct2stCd));
        setMokCd(String(item.bgtSbjct3stCd));
        setSummary(String(item.abstrCn));
        setMemo(String(item.memo));
        setAmount(String(item.dpstAmt));
        setSpcAcntgSeCd(String(item.spcAcntgSeCd));
        setAtrzSttsCd(String(item.atrzSttsCd));
        setAtrzOpnnCn(String(item.atrzOpnnCn));

        setSavedSpcDpstFiles(res?.result?.listFile ?? []); 

        AppContext.hideSpinner();
      },
    });
  }, [spcAcntgDpstDocId]);

  const validateBase = () => {
    if (Util.isEmpty(atrzId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(atrzLineId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzLineId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(spcAcntgDpstDocId)) {
      AppContext.openAlert({ title: "알림", msg: "대상이 선택되지 않았습니다.", icon: "warning" });
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (confirming) return;
    if (!validateBase()) return;

    setConfirming(true);
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/atrz/process-aprvr-idnty-rjct.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        atrzId: String(atrzId),
        atrzLineId: String(atrzLineId),
        atrzLinkDocId: String(spcAcntgDpstDocId),
        atrzLinkDocIdntySeCd: "01",
      },
      onSuccessFn: () => {
        AppContext.hideSpinner();
        setConfirming(false);

        AppContext.openAlert({ title: "알림", msg: "확인 처리되었습니다.", icon: "info" });
        if (typeof setCallTime === "function") setCallTime(new Date());
        onDone?.();
      },
    });
  };

  const openReject = () => {
    if (!validateBase()) return;
    setRejectModalOpen(true);
  };

  return (
    <div className="w100p dpFlx jcC pt5">
      <div className="dpFlx aiFs">
        <div className="w400">
          <div>
              <span className="fm-b fs16 fc007bff ls1 fw700">수입결의서 상세내용</span>
              <span className="fm-b fs13 ls06 fc777777" style={{marginLeft: "110px"}}>{dpstRsdocNo}</span>
          </div>
          {/* 1행: 수입일자 / 금액 (196 + 196) */}
          <div className="dpFlx mb5 pt10" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">수입일자</span>
              </div>
              <DatePickerBox
                value={depositDt}
                width={196}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                bgColor="#F6F6F6"
                textAlign="left"
                calendarThemeColor={AppContext.themeColor}
                onChange={(d: string) => setDepositDt(d)}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">금액</span>
              </div>
              <div className="h36 dpFlx aiC bsBdb" style={{ width: 196 }}>
                <NumberTextBox
                  value={amount}
                  className="h36 bd1 bdrd2 bgcFFE6CE"
                  style={{ width: 196 }}
                  thousandsGroupStyle="thousand"
                  thousandSeparator=","
                  placeholder="0"
                  fontSize={14}
                  fontFamily="fm-c"
                  letterSpacing={-1}
                  inputClass="taR mr10"
                  onChange={(v: string) => setAmount(v)}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          {/* 2행: 관/항/목 (128 * 3) */}
          <div className="dpFlx mb5" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">관</span>
              </div>
              <SelectBox
                value={kwanCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt1CodeArray(spcAcntgSeCd, "01"))}
                onChangeFunc={(v: string) => {
                  setKwanCd(v);
                  setHangCd("");
                  setMokCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">항</span>
              </div>
              <SelectBox
                value={hangCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt2CodeArray(spcAcntgSeCd, kwanCd))}
                onChangeFunc={(v: string) => {
                  setHangCd(v);
                  setMokCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">목</span>
              </div>
              <SelectBox
                value={mokCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt3CodeArray(spcAcntgSeCd, hangCd))}
                onChangeFunc={(v: string) => setMokCd(v)}
                disabled={true}
              />
            </div>
          </div>

          {/* 적요 */}
          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">적요</span>
            </div>
            <TextBox
              value={summary}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setSummary(t)}
              mode="disabled"
            />
          </div>

          {/* 메모 */}
          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">메모</span>
            </div>
            <TextBox
              value={memo}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setMemo(t)}
              mode="disabled"
            />
          </div>

          <BudgetViewFile fileSeCd="B01" savedBudgetFiles={savedSpcDpstFiles} width="400px" height="70px" />

          {atrzSttsCd === "05" && (
            <div className="dpFlx flxdCol" style={{ width: 400 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">반려</span>
              </div>
              <div className="dpFlx aiFs">
                <div className="flxg1 bsBdb">
                  <textarea
                    value={atrzOpnnCn}
                    className="h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs13 fc333333 ls1 bgcF6F6F6 rszN"
                    style={{ width: "400px" }}
                    maxLength={2000}
                    placeholder=""
                    onChange={(e) => setAtrzOpnnCn(e.target.value)}
                    readOnly={true}
                  />
                </div>
              </div>
            </div>
          )}

          {/* 버튼 */}
          <div className="dpFlx jcR mt25 gap10">
            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="crimson"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName="반려"
              onClickFunc={openReject}
              disabled={confirming}
            />

            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="#007bff"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName={confirming ? "처리중..." : "확인"}
              onClickFunc={handleConfirm}
              disabled={confirming}
            />
          </div>

          {/* 반려사유 모달 */}
          <Modal
            modalOpen={rejectModalOpen}
            dim={true}
            visible={true}
            titleEl={<DialogTitleBar title="반려 사유 입력" backgroundColor={AppContext.themeColor} />}
            onCloseFunc={() => setRejectModalOpen(false)}
          >
            <BgtAtrzRejectReason
              atrzId={String(atrzId)}
              atrzLineId={String(atrzLineId)}
              atrzLinkDocId={String(spcAcntgDpstDocId)}
              setModalOpen={setRejectModalOpen}
              setCallTime={setCallTime}
              onDone={onDone}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default BgtSpcRtsoDpstDialog;
